/* Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { yupResolver } from "@hookform/resolvers/yup";
import { SubmitHandler, useForm } from "react-hook-form";
import { Input, Label, Spacer, Text, theme } from "@nordcloud/gnui";
import { ValidationError } from "~/generated/graphql";
import { FormGroup, FormWrap } from "~/components";
import { isNotEmpty } from "~/tools";
import { StepContainer } from "~/views/accounts/AddAccount/components/StepContainer";
import { ValidationErrors } from "~/views/accounts/AddCapacity/CapacityForm/components";
import {
  FormData,
  AwsFormFields,
  AwsFormSchema,
} from "~/views/cloudAccounts/BillingData/aws/validators";
import { AwsFormCFStackInfo } from "./AwsFormCFStackInfo";
import { AwsFormSupportInfo } from "./AwsFormSupportInfo";

type Props = {
  onSubmit: SubmitHandler<FormData>;
  onPrevStep: () => void;
  validationErrors?: ValidationError[];
};

export function AwsFormAuto({ onSubmit, onPrevStep, validationErrors }: Props) {
  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm<FormData>({
    resolver: yupResolver(AwsFormSchema),
    mode: "onChange",
    defaultValues: {
      region: "us-east-1",
      reportPathPrefix: "CUR",
      reportName: "CUR-Daily",
    },
  });

  const disableSubmit = !isValid || isNotEmpty(Object.keys(errors));

  return (
    <FormWrap
      footerProps={{
        nextButtonProps: {
          disabled: disableSubmit,
        },
        onPrevClick: onPrevStep,
      }}
      onSubmit={handleSubmit(onSubmit)}
    >
      <StepContainer counter={1}>
        <AwsFormCFStackInfo uri="https://docs.mca.nordcloudapp.com/docs/visibility/cloud-accounts/aws/add-billing-accounts#create-cost-and-usage-report-cur" />
      </StepContainer>

      <StepContainer counter={2}>
        <Text>Add this information to access your billing data:</Text>
        <ValidationErrors errs={validationErrors} />
        <Spacer height={theme.spacing.spacing04} />

        <FormGroup error={errors[AwsFormFields.ACCOUNT_ID]}>
          <Label
            required
            htmlFor={AwsFormFields.ACCOUNT_ID}
            name="Account ID"
          />
          <Input
            id={AwsFormFields.ACCOUNT_ID}
            {...register(AwsFormFields.ACCOUNT_ID)}
          />
        </FormGroup>

        <FormGroup error={errors[AwsFormFields.BUCKET_ID]}>
          <Label
            required
            htmlFor={AwsFormFields.BUCKET_ID}
            name="Bucket Name"
          />
          <Input
            id={AwsFormFields.BUCKET_ID}
            {...register(AwsFormFields.BUCKET_ID)}
          />
        </FormGroup>
      </StepContainer>

      <AwsFormSupportInfo />
    </FormWrap>
  );
}
